import { DropdownMenu as M } from 'radix-ui'
import { Check } from 'lucide-react'
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react'
import { cn } from '@/lib/utils'

export const DropdownMenu = M.Root
export const DropdownMenuTrigger = M.Trigger
export const DropdownMenuGroup = M.Group
export const DropdownMenuSub = M.Sub
export const DropdownMenuRadioGroup = M.RadioGroup

const contentClass =
  'z-50 min-w-44 overflow-hidden rounded-lg border border-border bg-surface p-1 text-sm shadow-md animate-in'

export const DropdownMenuContent = forwardRef<ElementRef<typeof M.Content>, ComponentPropsWithoutRef<typeof M.Content>>(
  ({ className, sideOffset = 4, align = 'start', ...props }, ref) => (
    <M.Portal>
      <M.Content ref={ref} sideOffset={sideOffset} align={align} className={cn(contentClass, className)} {...props} />
    </M.Portal>
  ),
)
DropdownMenuContent.displayName = 'DropdownMenuContent'

const itemClass =
  'relative flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-hover [&_svg]:size-4 [&_svg]:text-fg-subtle'

export const DropdownMenuItem = forwardRef<
  ElementRef<typeof M.Item>,
  ComponentPropsWithoutRef<typeof M.Item> & { destructive?: boolean }
>(({ className, destructive, ...props }, ref) => (
  <M.Item ref={ref} className={cn(itemClass, destructive && 'text-danger [&_svg]:text-danger', className)} {...props} />
))
DropdownMenuItem.displayName = 'DropdownMenuItem'

export const DropdownMenuCheckboxItem = forwardRef<
  ElementRef<typeof M.CheckboxItem>,
  ComponentPropsWithoutRef<typeof M.CheckboxItem>
>(({ className, children, ...props }, ref) => (
  <M.CheckboxItem ref={ref} className={cn(itemClass, 'pl-7', className)} {...props}>
    <span className="absolute left-2 flex size-4 items-center justify-center">
      <M.ItemIndicator>
        <Check className="size-3.5 text-fg" />
      </M.ItemIndicator>
    </span>
    {children}
  </M.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem'

export const DropdownMenuRadioItem = forwardRef<ElementRef<typeof M.RadioItem>, ComponentPropsWithoutRef<typeof M.RadioItem>>(
  ({ className, children, ...props }, ref) => (
    <M.RadioItem ref={ref} className={cn(itemClass, 'pl-7', className)} {...props}>
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <M.ItemIndicator>
          <Check className="size-3.5 text-fg" />
        </M.ItemIndicator>
      </span>
      {children}
    </M.RadioItem>
  ),
)
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem'

export function DropdownMenuLabel({ className, ...props }: ComponentPropsWithoutRef<typeof M.Label>) {
  return <M.Label className={cn('px-2 py-1.5 text-xs font-medium text-fg-subtle', className)} {...props} />
}

export function DropdownMenuSeparator({ className, ...props }: ComponentPropsWithoutRef<typeof M.Separator>) {
  return <M.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
}

export const DropdownMenuSubTrigger = forwardRef<ElementRef<typeof M.SubTrigger>, ComponentPropsWithoutRef<typeof M.SubTrigger>>(
  ({ className, ...props }, ref) => <M.SubTrigger ref={ref} className={cn(itemClass, 'data-[state=open]:bg-hover', className)} {...props} />,
)
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger'

export const DropdownMenuSubContent = forwardRef<ElementRef<typeof M.SubContent>, ComponentPropsWithoutRef<typeof M.SubContent>>(
  ({ className, ...props }, ref) => (
    <M.Portal>
      <M.SubContent ref={ref} className={cn(contentClass, className)} {...props} />
    </M.Portal>
  ),
)
DropdownMenuSubContent.displayName = 'DropdownMenuSubContent'
